import { useState, useEffect, ReactNode } from "react";
import { createPortal } from "react-dom";

interface DataTablePortalProps {
  id?: string;
  children: ReactNode;
}

/**
 * This component is responsible for rendering children within context of datatable using react portal
 * Children could be placed anywhere in the page, but will have access to data from datatable context
 */

const DataTablePortal = ({ id, children }: DataTablePortalProps) => {
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!id) return;

    const observer = new MutationObserver(() => {
      const element = document.getElementById(id);
      if (element) {
        setPortalElement(element);
        observer.disconnect(); // Stop observing once the element is found
      }
    });

    // Check if element already exists
    const existingElement = document.getElementById(id);
    if (existingElement) {
      setPortalElement(existingElement);
    } else {
      // Start observing for new elements
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      observer.disconnect(); // Cleanup observer on unmount
    };
  }, [id]);

  // If no id is provided, don't render anything
  if (!id) return null;

  // If portal element is found, render children in it
  return portalElement ? <>{createPortal(children, portalElement)}</> : null;
};

export default DataTablePortal;
